import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, X, Heart, User, LogOut, Shield, Inbox as InboxIcon, Download,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/cities", label: "Cities" },
  { to: "/directory", label: "Directory" },
  { to: "/available-now", label: "Available Now" },
  { to: "/requests", label: "Special Requests" },
  { to: "/forum", label: "Forum" },
  { to: "/agencies", label: "Agencies & Shops" },
  { to: "/membership", label: "Membership" },
  { to: "/contact", label: "Contact Us" },
];
const advertiserLink = { to: "/advertise", label: "Advertise" };

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // PWA install prompt capture
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => { setInstalled(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    // Detect standalone (already installed)
    if (window.matchMedia("(display-mode: standalone)").matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const install = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setInstallPrompt(null);
  }, [installPrompt]);

  const canAdvertise = !user || user.account_type === "advertiser" || user.role === "admin";
  const navLinks = canAdvertise ? [...links, advertiserLink] : links;

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-[100] transition-colors duration-500 ${
          scrolled || open
            ? "border-b border-white/5 bg-black/70 backdrop-blur-2xl"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-4 sm:px-6 sm:py-5 md:px-12">
          <Link to="/" data-testid="nav-logo" className="text-xl tracking-tight text-white sm:text-2xl">
            <Logo markSize={22} className="text-xl sm:text-2xl" />
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            {user && (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin" title="Admin"
                    className="p-2 text-white/70 transition-colors hover:text-velvet-gold">
                    <Shield size={18} />
                  </Link>
                )}
                <Link to="/inbox" data-testid="nav-inbox" title="Inbox"
                  className="p-2 text-white/70 transition-colors hover:text-velvet-gold">
                  <InboxIcon size={18} />
                </Link>
                <Link to="/account" data-testid="nav-account" title="Account"
                  className="p-2 text-white/70 transition-colors hover:text-velvet-gold">
                  <Heart size={18} />
                </Link>
              </>
            )}
            {!user && (
              <Link to="/login" data-testid="nav-login"
                className="flex items-center gap-2 rounded-full border border-white/20 px-3 py-2 text-[10px] uppercase tracking-luxe text-white transition-colors duration-500 hover:bg-white hover:text-black sm:px-5 sm:text-xs">
                <User size={14} /> <span className="hidden xs:inline">Sign In</span>
              </Link>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-menu-toggle"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-velvet-gold hover:text-velvet-gold"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Full-height drawer — same UX on mobile + desktop for consistency */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="nav-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-md"
            onClick={() => setOpen(false)}
            data-testid="nav-drawer-backdrop"
          />
        )}
        {open && (
          <motion.aside
            key="nav-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[97] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-velvet-coal px-6 pt-24 pb-8 shadow-2xl sm:px-10"
            data-testid="nav-drawer"
          >
            <nav className="flex flex-1 flex-col gap-1">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.04, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`nav-drawer-link-${l.label.toLowerCase()}`}
                    className={`block border-b border-white/5 py-4 font-heading text-2xl transition-colors sm:text-3xl ${
                      location.pathname === l.to
                        ? "text-velvet-gold"
                        : "text-white hover:text-velvet-rose"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="mt-6 space-y-4">
              {!installed && installPrompt && (
                <button
                  onClick={install}
                  data-testid="nav-install-app"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-velvet-gold bg-velvet-gold/10 px-5 py-3 text-xs uppercase tracking-luxe text-velvet-gold hover:bg-velvet-gold hover:text-black"
                >
                  <Download size={14} /> Install Vixera app
                </button>
              )}
              {installed && (
                <p data-testid="nav-app-installed" className="text-center text-[10px] uppercase tracking-luxe text-white/40">
                  App installed on this device
                </p>
              )}

              {user ? (
                <button
                  onClick={handleLogout}
                  data-testid="nav-drawer-logout"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 py-3 text-xs uppercase tracking-luxe text-white/80 hover:border-velvet-rose hover:text-velvet-rose"
                >
                  <LogOut size={14} /> Logout {user.username ? `(${user.username})` : ""}
                </button>
              ) : (
                <Link
                  to="/register"
                  data-testid="nav-drawer-register"
                  className="block w-full rounded-full bg-white py-3 text-center text-xs uppercase tracking-luxe text-black hover:bg-velvet-rose hover:text-white"
                >
                  Create account
                </Link>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
